import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import GlobalSearch from '../components/GlobalSearch';
import ClientView from '../components/lobbyists/ClientView';
import { IconButton } from '../components/Button';
import PageContainer from '../layout/PageContainer';
import { useBreakpoint } from '../useBreakpoint';
import useUrlParams from '../hooks/useUrlParams';
import { SEARCH_KINDS, SearchKind } from '../data/search';
import { space } from '../theme';

// /search?q=…&type=…: the search screen. The query lives in the URL, so Back from a result
// returns to the same results, and a search can be shared as a link. On phones the page's
// own header (back + field) takes the place of the top bar (MobileShell hides it here).
// /search?client=…: a client of registered lobbyists, opened from a "לקוחות של שדלנים" result:
// who represents it, by firm.
const SearchPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop } = useBreakpoint();
  const [params, setParams] = useUrlParams();
  const query = params.get('q') || '';
  const typeParam = params.get('type') as SearchKind | null;
  const type = typeParam && SEARCH_KINDS.indexOf(typeParam) >= 0 ? typeParam : null;
  const client = params.get('client');

  const update = (updates: Record<string, string | null>) => setParams(prev => {
    const next = new URLSearchParams(prev);
    Object.keys(updates).forEach(k => { const v = updates[k]; if (v === null) next.delete(k); else next.set(k, v); });
    return next;
  });

  // Back to wherever search was opened from; a direct link has no in-app history, so go home
  // (or, from a client, to search)
  const back = (
    <IconButton
      icon="chevronBack"
      label="חזרה"
      onClick={() => (location.key !== 'default' ? navigate(-1) : navigate(client !== null ? '/search' : '/parties', { replace: true }))}
    />
  );

  if (client !== null) {
    return (
      <Layout width="full" breadcrumbs={[{ label: 'חיפוש', to: '/search' }, { label: client || 'לקוח' }]}>
        <PageContainer>
          <div style={{ padding: isDesktop ? 0 : `0 ${space.lg}px ${space.lg}px`, maxWidth: isDesktop ? 760 : undefined }}>
            <ClientView key={client} name={client} leading={isDesktop ? undefined : back} />
          </div>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout width="full" breadcrumbs={[{ label: 'חיפוש' }]}>
      <PageContainer>
        <div style={{ padding: isDesktop ? 0 : `0 ${space.lg}px ${space.lg}px`, maxWidth: isDesktop ? 760 : undefined }}>
          <GlobalSearch
            variant="page"
            query={query}
            onQueryChange={q => update({ q: q || null, type: null })}
            type={type}
            onTypeChange={t => update({ type: t })}
            leading={isDesktop ? undefined : back}
            // Opening search raises the keyboard; returning to results with Back doesn't
            autoFocus={!query}
          />
        </div>
      </PageContainer>
    </Layout>
  );
};

export default SearchPage;
